import { useMemo, useState } from 'react';

import type { AdminStore } from '@/client/types/stores';
import type { StoreAttributeField } from '@/client/types/stores/parity';
import { useStoreAttributeSpecQuery, useStoreAttributesQuery } from '@/client/api/stores/parity.query';
import { useUpdateStoreAttributes } from '@/client/api/stores/parity.mutation';
import { PERMISSIONS } from '@/client/config/permissions';
import { useGate } from '@/client/hooks/useGate';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

import { SectionCard, CardSkeleton, FormSkeleton } from '../shared';
import { ReasonDialog } from '../ReasonDialog';
import { SaveRow } from './controls';

/** Fields with their own editor elsewhere (Visit hours/pin, Delivery coverage). */
const DEDICATED = new Set(['operating_hours', 'location_latitude', 'location_longitude', 'delivery_locations']);

const asList = (v: unknown): string[] =>
  Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string' && !!x.trim()) : typeof v === 'string' && v.trim() ? [v] : [];
const asScalar = (v: unknown): string =>
  v == null ? '' : Array.isArray(v) ? String(v.find((x) => x != null) ?? '') : typeof v === 'object' ? '' : String(v);

const inputType = (f: StoreAttributeField) => {
  const fmt = (f.format ?? '').toLowerCase();
  if (fmt === 'email' || f.name === 'email') return 'email';
  if (fmt === 'url' || f.name === 'website') return 'url';
  if (fmt === 'phone' || f.name.includes('phone') || f.name === 'whatsapp') return 'tel';
  return 'text';
};

/** One attribute, rendered from its spec the way ff-ui's Store Manager does. */
const FieldInput: React.FC<{
  field: StoreAttributeField;
  value: unknown;
  disabled: boolean;
  onChange: (v: unknown) => void;
}> = ({ field, value, disabled, onChange }) => {
  const fmt = (field.format ?? '').toLowerCase();
  const options = field.options ?? [];
  const isChip = fmt.includes('chip') || (field.many && options.length > 0);

  if (isChip && options.length > 0) {
    const selected = new Set(asList(value));
    const atCap = field.max_values != null && selected.size >= field.max_values;
    return (
      <div className="flex flex-wrap gap-1.5">
        {options.map((opt) => {
          const on = selected.has(opt);
          return (
            <button
              key={opt}
              type="button"
              disabled={disabled || (!on && atCap)}
              onClick={() => {
                const next = new Set(selected);
                if (on) next.delete(opt);
                else next.add(opt);
                onChange(Array.from(next));
              }}
              className={cn(
                'rounded-full border px-2.5 py-1 text-xs font-medium transition-colors disabled:opacity-40',
                on ? 'border-teal-600 bg-teal-600 text-white' : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50',
              )}
            >
              {opt}
            </button>
          );
        })}
      </div>
    );
  }

  if (fmt.includes('select') && options.length > 0) {
    return (
      <select
        disabled={disabled}
        value={asScalar(value)}
        onChange={(e) => onChange(e.target.value || null)}
        className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
      >
        <option value="">Not set</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }

  if (field.many) {
    return (
      <Input
        disabled={disabled}
        value={asList(value).join(', ')}
        placeholder="Comma-separated"
        onChange={(e) => onChange(e.target.value.split(',').map((x) => x.trim()).filter(Boolean))}
      />
    );
  }

  if (/json|coordinate/.test(fmt)) {
    return (
      <textarea
        disabled={disabled}
        rows={3}
        value={typeof value === 'string' ? value : value == null ? '' : JSON.stringify(value, null, 2)}
        onChange={(e) => {
          try {
            onChange(JSON.parse(e.target.value));
          } catch {
            onChange(e.target.value);
          }
        }}
        className="w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-xs"
      />
    );
  }

  return (
    <Input
      type={inputType(field)}
      disabled={disabled}
      maxLength={field.max_length ?? undefined}
      value={asScalar(value)}
      onChange={(e) => onChange(e.target.value)}
    />
  );
};

/**
 * Storefront attributes for one or more spec groups (contact, social, visit,
 * shopping, taxonomy, delivery), rendered from `GET /admin/stores/attributes/spec`
 * and saved through the seller's validation (`PUT /<id>/attributes`).
 */
export const AttributesSection: React.FC<{
  store: AdminStore;
  groups: string[];
  title: string;
  description?: string;
}> = ({ store, groups, title, description }) => {
  const { data: spec, isLoading: loadingSpec } = useStoreAttributeSpecQuery();
  const { data, isLoading } = useStoreAttributesQuery(store.storeid);
  const save = useUpdateStoreAttributes();
  const canEdit = useGate(PERMISSIONS.store.profile).allowed;
  const [draft, setDraft] = useState<Record<string, unknown>>({});
  const [confirm, setConfirm] = useState(false);

  const sections = useMemo(
    () =>
      (spec?.groups ?? [])
        .filter((g) => groups.includes(g.group))
        .map((g) => ({ ...g, fields: g.fields.filter((f) => !DEDICATED.has(f.name)) }))
        .filter((g) => g.fields.length > 0),
    [spec, groups],
  );

  if (loadingSpec || isLoading) return <CardSkeleton description><FormSkeleton fields={4} /></CardSkeleton>;

  const current = data?.attributes ?? {};
  const valueOf = (name: string) => (name in draft ? draft[name] : current[name]);
  const changed = Object.keys(draft).filter((k) => JSON.stringify(draft[k]) !== JSON.stringify(current[k] ?? null));

  return (
    <div className="space-y-4">
      {sections.length === 0 && (
        <SectionCard title={title}>
          <p className="text-sm text-gray-400">No attributes are configured for this section.</p>
        </SectionCard>
      )}
      {sections.map((g, i) => (
        <SectionCard key={g.group} title={i === 0 ? title : g.label} description={i === 0 ? description : undefined}>
          <div className="space-y-4">
            {g.fields.map((f) => (
              <label key={f.name} className="block space-y-1">
                <span className="flex items-center gap-1 text-sm font-medium text-gray-900">
                  {f.label}
                  {f.required && <span className="text-red-500">*</span>}
                  {f.max_values != null && <span className="text-xs font-normal text-gray-400">(max {f.max_values})</span>}
                </span>
                {f.description && <span className="block text-xs text-gray-500">{f.description}</span>}
                <FieldInput
                  field={f}
                  value={valueOf(f.name)}
                  disabled={!canEdit || f.read_only}
                  onChange={(v) => setDraft((d) => ({ ...d, [f.name]: v }))}
                />
              </label>
            ))}
          </div>
          {i === sections.length - 1 && (
            <SaveRow dirty={changed.length > 0} saving={save.isPending} onSave={() => setConfirm(true)} onReset={() => setDraft({})} />
          )}
        </SectionCard>
      ))}

      {confirm && (
        <ReasonDialog
          open
          title={`Save ${changed.length} field${changed.length === 1 ? '' : 's'}`}
          description={`Fields: ${changed.join(', ')}. Validated against the attribute catalogue; the seller is notified.`}
          confirmLabel="Save"
          reasonRequired
          loading={save.isPending}
          onConfirm={(reason) =>
            save.mutate(
              { storeid: store.storeid, reason, attributes: Object.fromEntries(changed.map((k) => [k, draft[k]])) },
              { onSuccess: () => { setConfirm(false); setDraft({}); } },
            )
          }
          onClose={() => setConfirm(false)}
        />
      )}
    </div>
  );
};
